import { differenceInMilliseconds, parse } from 'date-fns';
import * as Sentry from '@sentry/browser';
import Cookies from 'universal-cookie';

import { isNewCardDashQueryParamSet, isReactIntegrationEnvironment, JWT_TOKEN_KEY, LS_ENABLED_CARD_DASH } from '../util';

import UrlRouter from './urlRouter';
import { safeStringCompare } from './safeStringCompare';
import heapHelper from './heapHelper';

interface IJWTPayloadBase {
  exp: number;
  iat: number;
  apiBaseUrl?: string | null;
  customerID?: string;
  customerUUID?: string;
  customerApplicationUUID?: string;
  enableNewCardDash?: boolean;
}

enum SessionRedirectRoute {
  Apply = 'apply',
  MerchantDecline = 'merchant_decline',
}

/**
 * Result of a POST /customer_dash_token bootstrap attempt (GE-3271).
 *
 * The backend returns several distinct `401 { reauthenticate: true }` shapes.
 * We branch on the `error`/`result` fields to tell a *recoverable* expiry (the
 * short-lived JWT lapsed but the avant-basic web session is almost certainly
 * still valid, so the login/handoff will silently re-mint a fresh cookie) apart
 * from a *non-recoverable* failure (the web session itself is gone, so the user
 * will actually have to log in). See {@link bootstrapTokenFromCookie}.
 *
 * - `success`             — 200 with a token; session was seeded.
 * - `reauthenticate`      — recoverable 401: `token_expired`, or
 *                           `authentication_failed` with `result: 'no_token'`
 *                           (cookie wasn't sent). The backend has already
 *                           cleared the stale cookie. The client should route
 *                           through login/handoff, which is expected to succeed
 *                           silently — no scary "please log in" prompt.
 * - `reauthenticateFailed`— non-recoverable 401: `authentication_failed` with
 *                           `result` in {`invalidated`, `unknown`, `malformed`,
 *                           `failed`}. Silent re-mint will NOT work; this is the
 *                           only case where a genuine login prompt is warranted.
 * - `unavailable`         — endpoint off / not reachable (e.g. 404 flag-off,
 *                           403 origin, network error). Fall back to normal
 *                           flow.
 */
export enum BootstrapResult {
  Success = 'success',
  Reauthenticate = 'reauthenticate',
  ReauthenticateFailed = 'reauthenticateFailed',
  Unavailable = 'unavailable',
}

/**
 * The `result` values avant-basic's customer_dash_token_controller returns
 * alongside `error: 'authentication_failed'`. Only `no_token` is recoverable
 * via a silent re-mint (the cookie simply wasn't sent); the rest mean the
 * underlying web session is not usable, so a real login is required.
 */
export enum BootstrapAuthResult {
  NoToken = 'no_token',
  Invalidated = 'invalidated',
  Unknown = 'unknown',
  Malformed = 'malformed',
  Failed = 'failed',
}

interface IBootstrapErrorBody {
  reauthenticate?: boolean;
  error?: string;
  result?: BootstrapAuthResult | string;
}

interface IRedirectWithSession {
  requestData: {
    [key: string]: string;
    redirect_route: SessionRedirectRoute;
  };
  fallbackUrl?: string;
}

const safeDecode: <JWTPayload>(base64String: string) => JWTPayload | null = base64String => {
  try {
    return JSON.parse(atob(base64String));
  } catch (e) {
    return null;
  }
};

const cookies = new Cookies();

// tenant grace period
const WARNING_GRACE_PERIOD: number = AvantConfig.TenantConfig.logoutGracePeriod;

/**
 * 30 minutes in milliseconds
 */
const MAX_EXP_TIME: number = 1800000;
const MILLISECONDS: number = 1000;

// one minute in milliseconds
const UPDATE_STATUS_INTERVAL: number = 180000;

// GE-3271: client-side safety cap on bootstrap + re-auth attempts. Persisted in
// sessionStorage so it survives the full-page navigation that the re-auth
// handoff performs, and is cleared on a successful (200) bootstrap. Prevents a
// tight redirect loop if an environment is misconfigured.
const REAUTH_ATTEMPTS_KEY: string = 'customer-dash:reauth-attempts';
const MAX_REAUTH_ATTEMPTS: number = 2;
const UNAUTHORIZED_STATUS: number = 401;

const getLogoutMS: (expirationTime: Date) => number = expTime => Math.min(
  Math.abs(differenceInMilliseconds(expTime, new Date())),
  MAX_EXP_TIME
);

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const DEFAULT_API_URL: string = process.env.REACT_APP_API_URL!;

export const WARNING_EVENT: 'timer:warning' = 'timer:warning';
export const EXPIRED_EVENT: 'timer:expired' = 'timer:expired';
export const UPDATE_STATUS_EVENT: 'interval:update-status' = 'interval:update-status';

type Events =
  | typeof WARNING_EVENT
  | typeof EXPIRED_EVENT
  | typeof UPDATE_STATUS_EVENT;
type Callbacks = { [event in Events]: Function[] };
type Timers = { [event in Events]: number | null };

class SessionManager<JWTPayload extends IJWTPayloadBase = IJWTPayloadBase> {
  private _token: string | null = null;
  private readonly _timers: Timers = {
    [EXPIRED_EVENT]: null,
    [UPDATE_STATUS_EVENT]: null,
    [WARNING_EVENT]: null,
  };
  private readonly _callbacks: Callbacks = {
    [EXPIRED_EVENT]: [],
    [UPDATE_STATUS_EVENT]: [],
    [WARNING_EVENT]: [],
  };
  private readonly _urlRouter: UrlRouter = new UrlRouter(DEFAULT_API_URL);
  private _customerHasInteracted: boolean = false;

  constructor () {
    // Option 2b: the token is no longer sourced from the URL (?jwtToken=) or a
    // JS-readable cookie. Returning sessions seed from localStorage; a fresh
    // handoff is bootstrapped via bootstrapTokenFromCookie() (see index.tsx),
    // which reads the HttpOnly cookie server-side.
    this.setToken(localStorage.getItem(JWT_TOKEN_KEY));

    window.addEventListener('click', () => this.recordInteraction());
    window.addEventListener('keydown', () => this.recordInteraction());
  }

  public get token (): string | null {
    return this._token;
  }

  public get urlRouter (): UrlRouter {
    return this._urlRouter;
  }

  public get hasSession (): boolean {
    return !!this.token;
  }

  /**
   * Option 2b: obtain the token from the HttpOnly customer_dash_token cookie via
   * a credentialed request to the backend bootstrap endpoint. The cookie itself
   * is not readable by JS; the endpoint reads it server-side and returns the
   * token so we can seed the existing X-Avant-Token/localStorage session flow.
   *
   * GE-3271: the backend signals recoverable auth failures with a
   * `401 { reauthenticate: true }` after having deleted the stale cookie across
   * all domain scopes. We branch on the `error`/`result` fields:
   *   - `token_expired`, or `authentication_failed` + `result: 'no_token'`
   *     → {@link BootstrapResult.Reauthenticate} (recoverable; the handoff will
   *       silently re-mint a fresh cookie while the web session is valid).
   *   - `authentication_failed` + `result` in
   *     {invalidated|unknown|malformed|failed}
   *     → {@link BootstrapResult.ReauthenticateFailed} (non-recoverable; the
   *       user must genuinely log in).
   * The caller MUST route the user through login/handoff on `Reauthenticate`
   * and MUST NOT retry the bootstrap with the same cookie (that unchanged-cookie
   * retry is what produced the UAT4 loop).
   *
   * Safe to call when the backend flag is off: the endpoint responds 404 (or
   * 403 for a disallowed origin), we seed nothing, return `Unavailable`, and
   * existing localStorage behavior is preserved.
   *
   * @returns a {@link BootstrapResult} describing what the caller should do.
   */
  public async bootstrapTokenFromCookie (): Promise<BootstrapResult> {
    try {
      const response = await fetch(this._urlRouter.bootstrapTokenUrl, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        credentials: 'include',
      });

      if (response.ok) {
        const { token }: { token?: string } = await response.json();
        if (!token) {
          // Anomalous: backend returned 200 but no token in the body. Capture
          // as a warning so it surfaces rather than silently falling back.
          Sentry.captureMessage(
            'bootstrapTokenFromCookie: 200 response with no token in body',
            Sentry.Severity.Warning
          );

          return BootstrapResult.Unavailable;
        }

        this.setToken(token);
        Sentry.addBreadcrumb({
          category: 'auth',
          message: 'bootstrapTokenFromCookie: success',
          level: Sentry.Severity.Info,
        });

        return BootstrapResult.Success;
      }

      // A 401 with `reauthenticate: true` means the stale cookie was rejected
      // (and already deleted server-side). We branch on the `error`/`result`
      // fields so the *recoverable* expiry path (token_expired / no_token,
      // where the avant-basic web session is still valid and the handoff will
      // silently re-mint) is kept separate from the *non-recoverable* path
      // (authentication_failed with invalidated|unknown|malformed|failed),
      // where the user must genuinely log in.
      if (response.status === UNAUTHORIZED_STATUS) {
        const body: IBootstrapErrorBody = await response.json().catch(() => ({}));
        if (body?.reauthenticate === true) {
          const recoverable = this.isRecoverableReauth(body);
          Sentry.addBreadcrumb({
            category: 'auth',
            message: recoverable
              ? 'bootstrapTokenFromCookie: reauthenticate (recoverable; silent re-mint expected)'
              : 'bootstrapTokenFromCookie: reauthenticate failed (non-recoverable; login required)',
            level: Sentry.Severity.Info,
            // error/result are non-sensitive; log them to aid triage. No token
            // or cookie value is ever logged.
            data: { status: response.status, error: body.error, result: body.result },
          });
          // Do not cache or reuse a token across the reauthenticate boundary.
          this.logout();

          return recoverable ? BootstrapResult.Reauthenticate : BootstrapResult.ReauthenticateFailed;
        }
      }

      // 404 (flag off), 403 (origin), or any other non-ok response: fall back
      // to the normal unauthenticated flow.
      Sentry.addBreadcrumb({
        category: 'auth',
        message: 'bootstrapTokenFromCookie: unavailable',
        level: Sentry.Severity.Info,
        data: { status: response.status },
      });

      return BootstrapResult.Unavailable;
    } catch (e) {
      // Network / unexpected failure. Capture the exception so we can tell a
      // genuine outage apart from an expected non-ok response.
      Sentry.captureException(e);

      return BootstrapResult.Unavailable;
    }
  }

  /**
   * GE-3271: decide whether a `401 { reauthenticate: true }` is recoverable via
   * a silent login/handoff re-mint. Recoverable cases:
   *   - `error: 'token_expired'` — the short-lived JWT lapsed but the
   *     avant-basic web session is (almost certainly) still valid.
   *   - `error: 'authentication_failed', result: 'no_token'` — the cookie
   *     simply wasn't sent (SameSite/domain/Secure); the handoff can re-mint.
   * Everything else (`authentication_failed` with `invalidated`, `unknown`,
   * `malformed`, or `failed`) means the underlying session cannot mint a new
   * token, so the user must actually log in.
   *
   * Defensive default: if `error`/`result` are missing/unrecognized we treat
   * the reauth as recoverable, preserving the pre-branching behavior (attempt
   * the silent handoff) rather than showing a scary prompt on an unknown shape.
   */
  private isRecoverableReauth (body: IBootstrapErrorBody): boolean {
    if (body.error === 'authentication_failed') {
      return body.result === BootstrapAuthResult.NoToken;
    }

    // token_expired (or any other/absent error with reauthenticate: true) is
    // treated as recoverable.
    return true;
  }

  /**
   * GE-3271 safety cap. Returns true while the number of re-auth attempts for
   * the current page load is under {@link MAX_REAUTH_ATTEMPTS}. Persisted in
   * sessionStorage so the count survives the full-page navigation performed by
   * the re-auth handoff.
   */
  public canAttemptReauth (): boolean {
    return this.getReauthAttempts() < MAX_REAUTH_ATTEMPTS;
  }

  /**
   * GE-3271: navigate the user back through the avant-basic login/handoff entry
   * point, which re-mints a fresh customer_dash_token cookie. Increments the
   * safety-cap counter and preserves the current location so the user is
   * returned here after re-auth. Does NOT retry POST /customer_dash_token.
   */
  public redirectToReauth (): void {
    this.incrementReauthAttempts();

    Sentry.addBreadcrumb({
      category: 'auth',
      message: 'redirectToReauth: navigating to login/handoff to re-mint cookie',
      level: Sentry.Severity.Info,
      data: { attempt: this.getReauthAttempts() },
    });

    // Preserve the current location so the handoff can return the user here
    // (mirrors the existing unauthorized handling in apolloClient.ts).
    cookies.set('redirectAfterAuth', window.location.href, { path: '/', maxAge: 600000 });

    window.location.assign(this._urlRouter.loginUrl);
  }

  /**
   * GE-3271: called after a successful bootstrap to reset the safety-cap
   * counter so a later expiry within the same tab can re-auth again.
   */
  public clearReauthAttempts (): void {
    try {
      sessionStorage.removeItem(REAUTH_ATTEMPTS_KEY);
    } catch (e) {
      // sessionStorage may be unavailable (privacy mode); ignore.
    }
  }

  private getReauthAttempts (): number {
    try {
      const raw = sessionStorage.getItem(REAUTH_ATTEMPTS_KEY);
      const parsed = raw ? parseInt(raw, 10) : 0;

      return Number.isNaN(parsed) ? 0 : parsed;
    } catch (e) {
      return 0;
    }
  }

  private incrementReauthAttempts (): void {
    try {
      sessionStorage.setItem(REAUTH_ATTEMPTS_KEY, String(this.getReauthAttempts() + 1));
    } catch (e) {
      // sessionStorage may be unavailable (privacy mode); ignore.
    }
  }

  public setToken (token: string | null) {
    // Make sure we dont override a newer token with an older one.
    const newToken = this.ensureNewestToken(token);

    if (!newToken || newToken === this.token) { return; }
    this._token = newToken;
    this.setTimers();
    this.setApiBaseUrl();
    this.setHeapData();
    this.setEnableCardDash();
    localStorage.setItem(JWT_TOKEN_KEY, newToken);
  }

  public subscribe (event: Events, callback: Function) {
    if (!this._callbacks[event]) {
      return;
    }
    this._callbacks[event].push(callback);
  }

  public async refreshToken (): Promise<boolean> {
    try {
      const response = await fetch(
        this._urlRouter.refreshTokenUrl, {
          headers: {
            Accept: 'application/json',
            'X-Avant-Token': this.token || ''
          }
        }
      );
      const { refreshToken }: { refreshToken: string } = await response.json();
      if (!refreshToken) { throw new Error('Something went wrong'); }
      this.setToken(refreshToken);

      return true;
    } catch (e) {
      return false;
    }
  }

  public logout (): void {
    this.clearTimers();
    this._token = null;
    localStorage.removeItem(JWT_TOKEN_KEY);
    localStorage.removeItem(LS_ENABLED_CARD_DASH);

    Sentry.addBreadcrumb({
      category: 'auth',
      message: 'logout: cleared in-memory token and localStorage',
      level: Sentry.Severity.Info,
    });
  }

  public redirectWithSession: (props: IRedirectWithSession) => void = async ({ requestData, fallbackUrl }) => {
    if (!this.token) {
      if (fallbackUrl) {
        window.location.assign(fallbackUrl);
      }

      return;
    }

    try {
      const response = await fetch(this.urlRouter.bootstrapSession, {
        method: 'POST',
        body: JSON.stringify(requestData),
        headers: {
          'Content-Type': 'application/json',
          'X-Amount-Token': this.token
        },
        credentials: 'include'
      });

      if (response.ok) {
        const data: { url: string } = await response.json();
        window.location.assign(data.url);
      } else if (fallbackUrl) {
        window.location.assign(fallbackUrl);
      }
    } catch (e) {
      Sentry.captureException(e);
      if (fallbackUrl) {
        window.location.assign(fallbackUrl);
      }
    }
  }

  public redirectToApplicationWithSession: (appInfo: { uuid: string; id: string }) => void = async ({ uuid, id }) => {
    this.redirectWithSession({
      requestData: {
        customerApplicationUUID: uuid,
        redirect_route: SessionRedirectRoute.Apply,
      },
      fallbackUrl: this.urlRouter.getApplicationUrl(id)
    });
  }

  public redirectToMerchantDeclineWithSession: (appInfo: { uuid: string; id: string }) => void = async ({ uuid, id }) => {
    this.redirectWithSession({
      requestData: {
        customerApplicationUUID: uuid,
        redirect_route: SessionRedirectRoute.MerchantDecline,
      },
      fallbackUrl: this.urlRouter.getMerchantDeclineUrl(id)
    });
  }

  private setTimers () {
    this.clearTimers();

    const info = this.extractJWTPayload();
    if (!info) {
      return;
    }

    const parsedTime: Date = parse(info.exp * MILLISECONDS);
    const expiredMS: number = getLogoutMS(parsedTime);
    const warnMS: number = expiredMS - WARNING_GRACE_PERIOD;

    const warnTimer = window.setTimeout(() => this.warningTimer(), warnMS);
    const expiredTimer = window.setTimeout(() => this.sessionExpired(), expiredMS);
    const updateStatusTimer = window.setInterval(
      () => this.updateInteractionStatus(),
      UPDATE_STATUS_INTERVAL,
    );

    this._timers[WARNING_EVENT] = warnTimer;
    this._timers[EXPIRED_EVENT] = expiredTimer;
    this._timers[UPDATE_STATUS_EVENT] = updateStatusTimer;
  }

  private extractJWTPayload (): JWTPayload | null {
    if (!this._token) {
      return null;
    }

   return this.decodeJWT(this._token);
  }

  private decodeJWT(token: string): JWTPayload | null {
    const b64: string = token.split('.')[1];

    return safeDecode<JWTPayload>(b64);
  }


  private ensureNewestToken(newToken: string | null): string | null {
    const existingToken = localStorage.getItem(JWT_TOKEN_KEY);
    if (!existingToken || !newToken) return newToken || existingToken;

    const oldJWT = this.decodeJWT(existingToken) ;
    const newJWT = this.decodeJWT(newToken);

    // If either tokens do not parse as a JWT return the other token.
    if (!newJWT) return existingToken;
    if (!oldJWT) return newToken;

    // return the token issued latest.
    return oldJWT.iat < newJWT.iat ? newToken : existingToken;
  }

  private warningTimer () {
    this._emitEvent(WARNING_EVENT);
  }

  private sessionExpired () {
    this._emitEvent(EXPIRED_EVENT);
  }

  private clearTimers (): void {
    const expiredTimer = this._timers[EXPIRED_EVENT];
    const warningTimer = this._timers[WARNING_EVENT];
    const updateStatusTimer = this._timers[UPDATE_STATUS_EVENT];
    if (expiredTimer) { window.clearTimeout(expiredTimer); }
    if (warningTimer) { window.clearTimeout(warningTimer); }
    if (updateStatusTimer) { window.clearInterval(updateStatusTimer); }
    this._timers[EXPIRED_EVENT] = null;
    this._timers[WARNING_EVENT] = null;
    this._timers[UPDATE_STATUS_EVENT] = null;
  }

  private _emitEvent (event: Events): void {
    this._callbacks[event].forEach(fn => fn());
  }

  private setEnableCardDash () {
    const info = this.extractJWTPayload();
    if (info && info.enableNewCardDash) {
      localStorage.setItem(LS_ENABLED_CARD_DASH, info.enableNewCardDash.toString());
    } else if (isReactIntegrationEnvironment() && isNewCardDashQueryParamSet()) {
      localStorage.setItem(LS_ENABLED_CARD_DASH, 'true');
    }
  }

  private setApiBaseUrl () {
    // If not in integration environment, skip url lookup
    if (!isReactIntegrationEnvironment()) {
      return;
    }

    const info = this.extractJWTPayload();
    if (!info || !info.apiBaseUrl || safeStringCompare(info.apiBaseUrl, this._urlRouter.apiBaseUrl)) {
      return;
    }

    this._urlRouter.apiBaseUrl = info.apiBaseUrl;
  }

  private setHeapData () {
    const info = this.extractJWTPayload();
    if (!info || !info.customerID) {
      return;
    }

    heapHelper.identify(info.customerID);
  }

  private recordInteraction(): void {
    this._customerHasInteracted = true;
  }

  private updateInteractionStatus() {
    if (this._customerHasInteracted) {
      this._emitEvent(UPDATE_STATUS_EVENT);
    }
    this._customerHasInteracted = false;
  }
}

const sessionManager: SessionManager = new SessionManager();

export default sessionManager;
